import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import "../../styles/Form.css";
import logo from "../../images/logo.png";
import { toDateOnly } from "../../utils/dateOnly";

const BackgroundCheckFCRA = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const { id: paramId } = useParams();
  const effectiveUserId = isAdmin && paramId ? paramId : userId;

  const navigate = useNavigate();

  const TASK_ID = 58;
  const TASK_NAME = "background_check_fcra";

  const [user, setUser] = useState({});
  const [isReviewed, setIsReviewed] = useState(false);
  const [signedDate, setSignedDate] = useState("");

  const [position, setPosition] = useState("");
  const [otherPosition, setOtherPosition] = useState("");

  const [checks, setChecks] = useState({
    rights: false,
    mvr: false,
  });

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchData = async () => {
      const profileUrl = isAdmin
        ? `${API_BASE}/api/users/${effectiveUserId}`
        : `${API_BASE}/api/user/profile`;

      const [userRes, taskRes] = await Promise.all([
        axios.get(profileUrl, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE}/api/usertasks/status/${effectiveUserId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setUser(userRes.data);

      const answers =
        taskRes.data.answersByTaskId?.[TASK_ID] ||
        taskRes.data.answers?.[TASK_NAME];
      if (answers) {
        setChecks({
          rights: Number(answers.ut_q1) === 1,
          mvr: Number(answers.ut_q2) === 1,
        });

        if (answers.ut_q4) {
          const raw = answers.ut_q4.trim();

          if (raw.startsWith("Other")) {
            setPosition("Other");
            setOtherPosition(raw.split(":")[1]?.trim() || "");
          } else {
            // exact match for radio buttons
            setPosition(raw);
          }
        }
      }

      const status =
        taskRes.data.status?.[TASK_ID] ??
        taskRes.data.status?.[TASK_NAME] ??
        "not_completed";

      const signed =
        status === "review" ||
        status === "completed" ||
        status === "almost_expired";
      setIsReviewed(status === "completed" || status === "almost_expired");
      setSignedDate(
        signed
          ? taskRes.data.signdates?.[TASK_ID] ??
              taskRes.data.signdates?.[TASK_NAME] ??
              ""
          : "",
      );
    };

    fetchData();
  }, [API_BASE, effectiveUserId, isAdmin]);

  const readOnly = isReviewed;

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`;
  const displayedDate = signedDate
    ? new Date(signedDate).toLocaleDateString()
    : new Date().toLocaleDateString();

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  // const maskSIN = (sin) => (sin ? `***-**-${String(sin).slice(-4)}` : "");

  return (
    <>
      {/* 🔝 HEADER — matches Padlock */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span style={{ fontSize: 18, fontWeight: 500 }}>
            Background Check Authorization & Disclosure (FCRA)
          </span>
        </div>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4 print-area">
        {/* ===== Logo / Title / Address ===== */}
        <div className="row align-items-center mb-4">
          <div className="col-3">
            <img src={logo} alt="Logo" style={{ width: 150 }} />
          </div>
          <div className="col-6 text-center">
            <h3 className="m-0">Background Check Authorization</h3>
          </div>
          <CompanyAddress />
        </div>

        {/* ===== Applicant Info Card (Beautified like your image) ===== */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Driver Name</label>
                <input className="form-control" value={fullName} readOnly />
              </div>
              <div className="col-md-3">
                <label className="form-label">Social Security Number</label>
                <input className="form-control" value={user.sin} readOnly />
              </div>
              <div className="col-md-3">
                <label className="form-label">Date of Birth</label>
                <input
                  className="form-control"
                  value={toDateOnly(user.dob)}
                  readOnly
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Address</label>
                <input
                  className="form-control"
                  value={`${user.address1 || ""} ${user.address2 || ""}`}
                  readOnly
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">City</label>
                <input
                  className="form-control"
                  value={user.city || ""}
                  readOnly
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">State</label>
                <input
                  className="form-control"
                  value={user.province || ""}
                  readOnly
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">ZIP</label>
                <input
                  className="form-control"
                  value={user.postal_code || ""}
                  readOnly
                />
              </div>

              <div className="col-12">
                <label className="form-label mb-3">Position Applied For</label>
                <div>
                  {["Office/Administrative", "Driver (CDL)", "Other"].map(
                    (opt) => (
                      <label key={opt} className="me-4">
                        <input
                          type="radio"
                          className="me-1"
                          checked={position === opt}
                          disabled={readOnly}
                          onChange={() => setPosition(opt)}
                        />
                        {opt}
                      </label>
                    ),
                  )}
                </div>

                {position === "Other" && (
                  <input
                    className="form-control mt-3"
                    placeholder="Specify other position"
                    value={otherPosition}
                    onChange={(e) => setOtherPosition(e.target.value)}
                    disabled={readOnly}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ===== TEXT SECTIONS ===== */}

        <div className="mb-5">
          <h5 className="mb-3">1) Disclosure</h5>

          <p className="mb-3">
            New Way Transport Inc. (“Company”) may obtain a consumer report
            and/or investigative consumer report about you from a third-party
            consumer reporting agency (“CRA”) for employment purposes, including
            hiring, promotion, reassignment, retention, or termination.
          </p>

          <p className="mb-3">
            These reports may include information such as identity verification,
            employment history, education verification, criminal history (where
            permitted by law), driving record (if relevant), credit history
            (only if relevant and permitted), and other information bearing on
            character, general reputation, personal characteristics, or mode of
            living.
          </p>

          <p className="mb-0">
            You have the right to request the nature and scope of any
            investigative consumer report obtained.
          </p>
        </div>

        <div className="mb-5">
          <h5 className="mb-3">2) Authorization</h5>

          <p className="mb-3">
            I authorize the Company to obtain consumer reports and/or
            investigative consumer reports about me for employment purposes at
            any time during my application process and, if hired, throughout my
            employment, as permitted by law.
          </p>

          <p className="mb-0">
            I authorize any CRA, current or former employer, school, law
            enforcement agency, or other person/agency to provide information
            about me to the Company or its agents for lawful employment
            purposes.
          </p>
        </div>

        <div className="mb-5">
          <h5 className="mb-3">3) Applicant Certification</h5>

          <p className="mb-0">
            I certify that the information provided is true and complete to the
            best of my knowledge.
          </p>
        </div>

        <div className="mb-4">
          <h5 className="mb-3">5) Summary of Rights Acknowledgement</h5>

          <p className="mb-1">
            I acknowledge that I have received the document titled “A Summary of
            Your Rights Under the Fair Credit Reporting Act.”
          </p>

          <div className="form-check mt-1">
            <input
              className="form-check-input"
              type="checkbox"
              checked={checks.rights}
              disabled={readOnly}
              onChange={() => setChecks((p) => ({ ...p, rights: !p.rights }))}
            />
            <label className="form-check-label">I acknowledge</label>
          </div>
        </div>

        <div className="mb-4">
          <p className="mb-1">
            If the position involves operating a commercial motor vehicle, I
            authorize the Company to obtain my MVR (driving record), CDL status,
            and related safety performance history, as permitted by law.
          </p>

          <div className="form-check mt-1">
            <input
              className="form-check-input"
              type="checkbox"
              checked={checks.mvr}
              disabled={readOnly}
              onChange={() => setChecks((p) => ({ ...p, mvr: !p.mvr }))}
            />
            <label className="form-check-label">I acknowledge</label>
          </div>
        </div>

        <hr className="my-5" />

        {/* EMPLOYEE NAME + DATE */}
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label">Employee Name</label>
            <input
              type="text"
              className="form-control"
              value={fullName}
              readOnly
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Date</label>
            <input
              type="text"
              className="form-control"
              value={displayedDate}
              readOnly
            />
          </div>
        </div>

        <hr className="my-5" />

        {/* ===== Signature ===== */}
        <SignatureManager
          taskName={TASK_NAME}
          taskId={TASK_ID}
          userId={effectiveUserId}
          isAdmin={isAdmin}
          extraAnswers={{
            ut_q1: checks.rights ? 1 : 0,
            ut_q2: checks.mvr ? 1 : 0,
            ut_q4: position === "Other" ? `Other: ${otherPosition}` : position,
          }}
        />

        <ReviewManager
          taskName={TASK_NAME}
          taskId={TASK_ID}
          userId={effectiveUserId}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
};

export default BackgroundCheckFCRA;

